import { useState } from 'react';
import styled from 'styled-components';

const IMAGE_VERSION = '20260529-2';

const galleryImages = [
  {
    src: `/images/gallery/bild1.jpg?v=${IMAGE_VERSION}`,
    alt: 'Stadtheuriger zum Brückl Außenansicht'
  },
  {
    src: `/images/gallery/bild2.jpg?v=${IMAGE_VERSION}`,
    alt: 'Stadtheuriger zum Brückl Innenbereich'
  },
  {
    src: `/images/gallery/bild3.jpg?v=${IMAGE_VERSION}`,
    alt: 'Heurigen Atmosphäre'
  },
  {
    src: `/images/gallery/bild4.jpg?v=${IMAGE_VERSION}`,
    alt: 'Gastgarten und Sitzbereich'
  }
];

const Section = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem 3.5rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.25rem;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const Card = styled.button`
  position: relative;
  display: block;
  width: 100%;
  height: 360px;
  padding: 0;
  border: none;
  border-radius: 20px;
  overflow: hidden;
  background: #efe6d9;
  cursor: pointer;
  box-shadow: 0 16px 38px rgba(52, 38, 30, 0.12);
  transition:
    transform 0.25s ease,
    box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 22px 48px rgba(52, 38, 30, 0.18);
  }

  &:focus-visible {
    outline: 4px solid rgba(122, 31, 43, 0.28);
    outline-offset: 4px;
  }

  @media (max-width: 760px) {
    height: auto;
    cursor: default;

    &:hover {
      transform: none;
      box-shadow: 0 16px 38px rgba(52, 38, 30, 0.12);
    }
  }
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
  transition:
    transform 0.45s ease,
    filter 0.45s ease;

  ${Card}:hover & {
    transform: scale(1.035);
    filter: brightness(0.88);
  }

  @media (max-width: 760px) {
    height: auto;
    object-fit: contain;

    ${Card}:hover & {
      transform: none;
      filter: none;
    }
  }
`;

const Caption = styled.div`
  position: absolute;
  left: 1rem;
  right: 1rem;
  bottom: 1rem;
  padding: 0.8rem 0.95rem;
  border-radius: 14px;
  background: rgba(29, 22, 19, 0.62);
  color: #fff;
  font-weight: 700;
  line-height: 1.35;
  text-align: left;
  backdrop-filter: blur(8px);
  opacity: 0;
  transform: translateY(8px);
  transition:
    opacity 0.25s ease,
    transform 0.25s ease;

  ${Card}:hover & {
    opacity: 1;
    transform: translateY(0);
  }

  @media (max-width: 760px) {
    display: none;
  }
`;

const LightboxOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(22, 16, 14, 0.88);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
`;

const LightboxContent = styled.div`
  position: relative;
  width: min(1100px, 100%);
`;

const LightboxImage = styled.img`
  width: 100%;
  max-height: 84vh;
  object-fit: contain;
  display: block;
  border-radius: 20px;
  box-shadow: 0 28px 70px rgba(0, 0, 0, 0.42);
  background: #1d1613;
`;

const CloseButton = styled.button`
  position: absolute;
  top: -18px;
  right: -18px;
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 50%;
  background: #8b1e2d;
  color: #fff;
  font-size: 1.6rem;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.28);

  &:hover {
    background: #a6293a;
  }

  @media (max-width: 640px) {
    top: -12px;
    right: -8px;
  }
`;

const LightboxCaption = styled.div`
  margin-top: 0.85rem;
  color: #fff;
  text-align: center;
  font-weight: 700;
`;

function Gallery() {
  const [failedImages, setFailedImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);

  const visibleImages = galleryImages.filter(
    (image) => !failedImages.includes(image.src)
  );

  if (!visibleImages.length) return null;

  const handleImageError = (src) => {
    setFailedImages((prev) =>
      prev.includes(src) ? prev : [...prev, src]
    );
  };

  const openLightbox = (image) => {
    setSelectedImage(image);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  return (
    <Section>
      <Grid>
        {visibleImages.map((image, index) => (
          <Card
            key={image.src}
            type="button"
            onClick={() => openLightbox(image)}
            aria-label={`${image.alt} vergrößert anzeigen`}
          >
            <Image
              src={image.src}
              alt={image.alt}
              loading={index === 0 ? 'eager' : 'lazy'}
              onError={() => handleImageError(image.src)}
            />

            <Caption>{image.alt}</Caption>
          </Card>
        ))}
      </Grid>

      {selectedImage && (
        <LightboxOverlay onClick={closeLightbox}>
          <LightboxContent onClick={(event) => event.stopPropagation()}>
            <CloseButton
              type="button"
              onClick={closeLightbox}
              aria-label="Bild schließen"
            >
              ×
            </CloseButton>

            <LightboxImage
              src={selectedImage.src}
              alt={selectedImage.alt}
            />

            <LightboxCaption>{selectedImage.alt}</LightboxCaption>
          </LightboxContent>
        </LightboxOverlay>
      )}
    </Section>
  );
}

export default Gallery;